<template>
  <div id="app">
    <Header />
    <router-view />
  </div>
</template>

<style>
#app {
  min-height: 100vh;
  /* 手机端用实时可见高度（JS 写入），地址栏收起/展开时跟随变化；桌面不受影响 */
  min-height: var(--app-height, 100vh);
  display: flex;
  flex-direction: column;
  /* iPhone 四边安全区补偿 */
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
}

/*#app > *:last-child {
  margin-top: auto;
}*/
</style>

<script>
import Header from './components/Header.vue'

export default {
  name: 'App',
  components: { Header },
  // 滚动吸附与可见高度同步的运行时状态（不参与渲染）
  data() {
    return {
      isScrolling: false,   // 页面滚动/动量过程中（scroll 事件静默 140ms 视为停止）
      isTouching: false,    // 手指按住屏幕期间不做吸附纠偏，避免与拖动抢夺滚动位置
      heightPending: false, // 滚动期间收到 resize 时挂起的可见高度同步
      scrollEndTimer: null, // 滚动停止判定定时器
      lastAppHeight: 0      // 上次写入的 --app-height 值，未变化不重复写入
    }
  },
  created() {
    // 尽早把当前语言同步到 <html lang> 与页面标题/描述，避免首屏沿用上一语言的元信息
    this.syncLang()
    this.$watch(() => this.$i18n.locale, this.syncLang)
  },
  mounted() {
    // 整屏滚动吸附全局统一管理（桌面/移动端均开启，各模块保持整屏高度）
    this.updateSnap()
    this.unwatchRoute = this.$watch(() => this.$route.path, this.updateSnap)
    // 手机浏览器实际可见高度受地址栏收起/展开、系统状态栏影响，100vh 按“最大视口”算会高于可见区，
    // 导致整屏模块底部被裁切、吸附错位；这里实时读可见高度写入 --app-height，各页面用它替代 100vh
    this.updateAppHeight()
    // 手机滑动时地址栏收/展会连续触发 resize：若滚动过程中实时改写 --app-height，
    // 模块高度与全部吸附点会跟着位移，表现为滑动卡顿、松手后吸附不到位；
    // 因此滚动/触摸期间只挂起更新，滚动停止后再统一同步高度，并把当前屏纠正到最近吸附点
    window.addEventListener('resize', this.onViewportChange)
    window.addEventListener('orientationchange', this.onOrientationChange)
    window.addEventListener('scroll', this.onPageScroll, { passive: true })
    window.addEventListener('touchstart', this.onTouchStart, { passive: true })
    window.addEventListener('touchend', this.onTouchEnd, { passive: true })
    window.addEventListener('touchcancel', this.onTouchEnd, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onViewportChange)
    window.removeEventListener('orientationchange', this.onOrientationChange)
    window.removeEventListener('scroll', this.onPageScroll)
    window.removeEventListener('touchstart', this.onTouchStart)
    window.removeEventListener('touchend', this.onTouchEnd)
    window.removeEventListener('touchcancel', this.onTouchEnd)
    clearTimeout(this.scrollEndTimer)
    if (this.unwatchRoute) {
      this.unwatchRoute()
    }
    document.documentElement.style.scrollSnapType = ''
    document.body.style.scrollSnapType = ''
  },
  methods: {
    syncLang() {
      // <html lang> 供 CSS 的 :lang(en)/:lang(zh-CN) 选择器使用，实现英文文案单独调整字号与位置
      document.documentElement.lang = this.$i18n.locale === 'zh' ? 'zh-CN' : 'en'
      // 浏览器标签页标题、分享卡片标题（og:title）与页面描述随语言实时更新；静态 HTML 中的对应内容由构建模式输出
      // （build:en/build:cn，分享卡片等不执行 JS 的爬虫读取静态值），此处同步保证运行时
      // 与域名语言一致（构建产物与域名不匹配时自动纠正）
      const title = this.$t('pageTitle')
      const shareTitle = this.$t('pageShareTitle')
      const desc = this.$t('pageDescription')
      document.title = title
      this.updateMeta('name', 'description', desc)
      this.updateMeta('property', 'og:title', shareTitle)
      this.updateMeta('property', 'og:description', desc)
    },
    // 更新 head 中的 meta 标签内容，不存在则创建
    updateMeta(attr, key, content) {
      let el = document.head.querySelector('meta[' + attr + '="' + key + '"]')
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }
      el.setAttribute('content', content)
    },
    // 实时可见高度写入 CSS 变量（innerHeight 已扣除浏览器 UI，再减去设备安全区得到真正有效屏幕高度）；
    // 只有高度实际变化才写入并返回 true，避免滚动/缩放过程中反复无谓重排
    updateAppHeight() {
      const style = getComputedStyle(document.documentElement)
      const topInset = parseFloat(style.getPropertyValue('--safe-top')) || 0
      const bottomInset = parseFloat(style.getPropertyValue('--safe-bottom')) || 0
      const effectiveHeight = window.innerHeight - topInset - bottomInset
      if (effectiveHeight === this.lastAppHeight) {
        return false
      }
      this.lastAppHeight = effectiveHeight
      document.documentElement.style.setProperty('--app-height', effectiveHeight + 'px')
      return true
    },
    // 同步挂起的可见高度：高度变化会让全部吸附点整体位移，需顺带纠正当前屏位置
    syncAppHeight() {
      this.heightPending = false
      if (this.updateAppHeight()) {
        this.fixSnapAlignment()
      }
    },
    // 视口尺寸变化（地址栏收/展、键盘弹出、窗口缩放）：滚动/触摸中挂起等待，静止时防抖同步
    onViewportChange() {
      this.heightPending = true
      if (this.isScrolling || this.isTouching) {
        return
      }
      this.scheduleScrollEnd()
    },
    // 横竖屏切换会重置滚动状态：清掉进行中标记，走滚停流程等尺寸稳定后统一同步
    onOrientationChange() {
      this.heightPending = true
      this.isScrolling = false
      this.isTouching = false
      this.scheduleScrollEnd()
    },
    onPageScroll() {
      this.isScrolling = true
      this.scheduleScrollEnd()
    },
    // 触摸按下/抬起（touchcancel 兼容滚动被系统手势接管）：按住期间不做判定，避免与拖动抢夺位置
    onTouchStart() {
      this.isTouching = true
    },
    onTouchEnd() {
      this.isTouching = false
      this.scheduleScrollEnd()
    },
    // 滚动停止判定：scroll 静默或手指抬起 140ms 后触发，先同步挂起的高度，再兜底纠正吸附位置
    scheduleScrollEnd() {
      clearTimeout(this.scrollEndTimer)
      this.scrollEndTimer = setTimeout(() => {
        if (this.isTouching) {
          return
        }
        this.isScrolling = false
        if (this.heightPending) {
          this.syncAppHeight()
        }
        this.fixSnapAlignment()
      }, 140)
    },
    // 兜底纠偏：滚动停止后若视口未对准任一吸附点（页面顶/各整屏模块顶/页面底），平滑滚回最近吸附点；
    // 偏差很小时不处理，避免与浏览器原生吸附互相干扰（旧浏览器不支持平滑滚动时会直接跳到位）
    fixSnapAlignment() {
      if (!this.isSnapPage()) {
        return
      }
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight
      if (maxScroll <= 0) {
        return
      }
      const points = [0, maxScroll]
      document.querySelectorAll('.fullscreen-section').forEach((el) => {
        const top = window.scrollY + el.getBoundingClientRect().top
        points.push(Math.min(maxScroll, Math.max(0, top)))
      })
      let nearest = points[0]
      points.forEach((point) => {
        if (Math.abs(point - window.scrollY) < Math.abs(nearest - window.scrollY)) {
          nearest = point
        }
      })
      if (Math.abs(nearest - window.scrollY) < 3) {
        return
      }
      window.scrollTo({ top: nearest, behavior: 'smooth' })
    },
    // 是否启用整屏吸附的页面
    isSnapPage() {
      return ['/', '/product', '/service', '/download', '/about'].indexOf(this.$route.path) > -1
    },
    // 滚动吸附统一控制：整屏滚动页面在桌面与移动端均开启（模块均为 100vh 整屏高度）；
    // 各页面 mounted 晚于本组件执行、beforeDestroy 早于路由监听触发，此处始终为最终状态兜底；
    // 若后续某模块手机端高度不再恰好一屏，可在此加断点判断单独关闭移动端吸附
    updateSnap() {
      const value = this.isSnapPage() ? 'y mandatory' : ''
      document.documentElement.style.scrollSnapType = value
      document.body.style.scrollSnapType = value
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  height: 100%;
  /* 把设备安全区写入 CSS 变量，供 JS 读取计算有效屏幕高度 */
  --safe-top: env(safe-area-inset-top, 0px);
  --safe-bottom: env(safe-area-inset-bottom, 0px);
  --safe-left: env(safe-area-inset-left, 0px);
  --safe-right: env(safe-area-inset-right, 0px);
}

body {
  font-family: 'Noto Sans SC', 'PingFang SC', sans-serif;
  overflow-x: hidden;
}

/* i18n 文案中的 \n 全局生效换行；标签选择器优先级低于类选择器，
   个别元素已设置的 white-space: nowrap 不会被覆盖 */
h1, h2, h3, h4, h5, h6, p, span, a, li, td, th, button, label {
  white-space: pre-line;
}

/* 图片不可选中：避免鼠标拖选文字时图片被一并高亮 */
img {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* 隐藏滚动条但保留滚动功能 */
::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

html {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
</style>
